import type { IncomingMessage } from "http";
import type { NextApiRequest } from "next";
import { NextRequest } from "next/server";

import { getMultiCommunityOrigin } from "./community";

type Request = IncomingMessage | NextApiRequest;

/**
 * Resolves to the correct href for the current origin for absolute, relative and cross-community links.
 *
 * For links within the same community, returns a relative path.
 * For cross-community links, returns a fully qualified URL with the correct origin.
 * For absolute links to external sites, returns the original URL unchanged.
 *
 * Internal links are identified by:
 * - Being explicitly marked as cross-community
 * - Having the same origin as the current origin
 * - Having a hostname that includes either the href community domain or apex domain of the current network community
 *   - Note: the apex domain value should come from `NETWORK_COMMUNITY_DATA` and will always use .com as the TLD
 * - Having a hostname that includes the voxops.net variant of either href community domain or current apex domain
 *
 * @param href - The original href (can be a full URL or just a path)
 * @param currentApexDomain - The "domain" value (apex domain) of the current network community (used for internal link detection)
 * @param currentOrigin - The current site's origin
 * @param isCrossCommunity - Whether or not the link is to another community within the network
 * @param hrefCommunityDomain - Domain of the target community
 * @param hrefCommunitySubdomain - Subdomain of the target community
 * @returns Either a relative path (for same community) or a rewritten URL (for different community)
 */
export const getResolvedHref = ({
  href,
  currentOrigin,
  currentApexDomain,
  isCrossCommunity,
  hrefCommunityDomain,
  hrefCommunitySubdomain,
}: {
  href: string;
  currentOrigin: string;
  currentApexDomain: string;
  isCrossCommunity?: boolean;
  hrefCommunityDomain?: string;
  hrefCommunitySubdomain?: string | null;
}): string => {
  // Href is a relative path already, just return it
  if (!href.startsWith("http")) {
    return href;
  }

  const url = new URL(href, currentOrigin);
  const { pathname, search, hash } = url;

  const isInternalHostname =
    isCrossCommunity ||
    url.origin === currentOrigin ||
    url.hostname.includes(hrefCommunityDomain || currentApexDomain) ||
    url.hostname.includes(
      (hrefCommunityDomain || currentApexDomain).replace(
        ".com",
        ".duet.voxops.net",
      ),
    );

  /**
   * If it's an external URL return full URL:
   * - not cross-community
   * - not same origin
   * - not same origin for different environment
   */
  if (!isInternalHostname) {
    return href;
  }

  // The href is for the current community
  if (!isCrossCommunity) {
    // Construct a relative path
    return `${pathname}${search}${hash}`;
  }

  // The href is for a different community within the same network
  const hrefOrigin = getMultiCommunityOrigin({
    origin: currentOrigin,
    targetSubdomain: hrefCommunitySubdomain,
    targetDomain: hrefCommunityDomain,
  });

  /**
   * Construct a new URL with the href origin. The only reason we do this
   * instead of just using the original href is because hrefOrigin is more
   * reliable than the origin we get from WordPress permalinks in non-prod
   * environments. E.g. we may have cases on a voxops origin where links
   * from WordPress take us to a Fastly origin.
   */
  return new URL(`${pathname}${search}${hash}`, hrefOrigin).href;
};

/**
 * Constructs a relative URL based on the provided absolute URL and community origin.
 * If the provided URL has the same origin as the community origin, returns a relative URL.
 * Otherwise, returns the original URL unchanged.
 *
 * @param url The absolute URL to construct a relative URL from.
 * @param siteOrigin The origin for the community site. If the run-time community config value
 * of siteOrigin (dynamically determined from the WordPress siteDomain via the API) is available
 * this should be preferred over the build-time community config value of communityOrigin. It
 * should always match the origin in the permalinks for the community site in WordPress.
 * @returns The constructed relative URL or the original URL if it doesn't match the community origin.
 */
export const getRelativeURL = (url: string, siteOrigin: string): string => {
  // Parse the provided URL relative to the community origin
  const newUrl = new URL(url, siteOrigin);

  // Check if the parsed URL matches the community origin
  return newUrl.origin === siteOrigin
    ? `${newUrl.pathname}${newUrl.search}${newUrl.hash}` // Return relative URL
    : url; // Return original URL
};

/**
 * Return domain apex. E.g. if `domain` is `www.theverge.com`, this returns `theverge.com`.
 */
export function getDomainApex(domain: string): string {
  const parts = domain.split(".");
  if (parts.length > 2) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return `${parts[parts.length - 2]!}.${parts[parts.length - 1]!}`;
  }
  return domain;
}

/**
 * Get Host from request while allowing proxy to overwrite to `communityDomain`.
 */
export function getHost(
  req: Request | NextRequest,
  communityDomain: string,
): string {
  // Handle NextRequest
  if ("nextUrl" in req) {
    const host = req.headers.get("host");
    if (!host) {
      throw new Error("missing host header");
    }

    const forwardedHost = req.headers.get("x-duet-forwarded-host");

    // Allow proxy to change host, when forwarded host has the same domain apex as the community domain.
    // This means e.g. if the community domain is `www.theverge.com` we allow changing host to any subdomain
    // `*.theverge.com` or just `theverge.com`. This way we can support `duet-staging.theverge.com`.
    const allowChangingHost =
      forwardedHost &&
      getDomainApex(forwardedHost) === getDomainApex(communityDomain);

    return allowChangingHost ? forwardedHost : host;
  }

  // Handle standard Request
  if (!req.headers.host) {
    throw new Error("missing host header");
  }

  const forwardedHost =
    typeof req.headers["x-duet-forwarded-host"] === "string" &&
    req.headers["x-duet-forwarded-host"];

  // Allow proxy to change host, when forwarded host has the same domain apex as the community domain.
  // This means e.g. if the community domain is `www.theverge.com` we allow changing host to any subdomain
  // `*.theverge.com` or just `theverge.com`. This way we can support `duet-staging.theverge.com`.
  const allowChangingHost =
    forwardedHost &&
    getDomainApex(forwardedHost) === getDomainApex(communityDomain);

  return allowChangingHost ? forwardedHost : req.headers.host;
}

/**
 * Return X-Forwarded Headers,
 * When multiple values exists, return the value from the first proxy.
 */
export function getFirstXForwarded(
  req: Request | NextRequest,
  kind: "proto" | "port" | "host" | "for",
) {
  const header = `x-forwarded-${kind}`;

  // Handle NextRequest
  if ("nextUrl" in req) {
    const value = req.headers.get(header);
    return value ? value.split(",")[0] : null;
  }

  // Handle standard Request
  if (typeof req.headers[header] !== "string") {
    return null;
  }
  return req.headers[header].split(",")[0];
}

export function reconstructAbsoluteURL({
  req,
  path,
  communityDomain,
}: {
  req: Request | NextRequest;
  path: string;
  communityDomain: string;
}): URL {
  const proto = getFirstXForwarded(req, "proto") === "https" ? "https" : "http";
  const host = getHost(req, communityDomain);

  return new URL(path, `${proto}://${host}`);
}

// Helper function for the admin-tools to get a req URL hostname on a given network.
export function moveHostnameToTargetNetwork(params: {
  hostname: string;
  targetNetwork: string;
  currentNetwork: string;
}): string {
  let { hostname, targetNetwork, currentNetwork } = params;

  if (!hostname.includes("localhost")) {
    targetNetwork = targetNetwork.replace(".com", "");
    currentNetwork = currentNetwork.replace(".com", "");
    hostname = hostname.replace(currentNetwork, targetNetwork);
  }

  return hostname;
}

/**
 * getEntryRevisionURL returns default/empty url, when url is not set.
 */
export function getEntryRevisionURL(url: string | undefined): string {
  return url ?? "";
}

export function absoluteURL(
  req?: Request | NextRequest,
  localhostAddress = "localhost:3000",
) {
  let host: string;

  if (req) {
    // NextRequest case
    if ("nextUrl" in req) {
      // Host header should always be available but fall back to host from nextUrl
      host = req.headers.get("host") || req.nextUrl.host;
    } else {
      // Standard Request case
      host = req.headers.host || localhostAddress;
    }
  } else {
    // Client-side case
    host =
      typeof window !== "undefined" ? window.location.host : localhostAddress;
  }

  /**
   * Use http protocol for development environments, https for all others
   * - localhost with optional port (e.g. localhost:3000)
   * - dev subdomain with optional sub-community and port (dev.eater.com:3000, dev.austin.eater.com:3000)
   *
   * Regex breakdown: ^(localhost(:\d+)?|dev\.[^.]+\.?[^.]+\.com(:\d+)?)$
   * - ^: Start of string
   * - First group matches either:
   *   - localhost(:\d+)?: "localhost" with optional port number
   *   - OR
   *   - dev\.[^.]+\.?[^.]+\.com(:\d+)?:
   *     - dev\.: Literal "dev."
   *     - [^.]+: One or more non-dot chars (domain)
   *     - \.?: Optional dot
   *     - [^.]+: One or more non-dot chars (optional sub-community)
   *     - \.com: Literal ".com"
   *     - (:\d+)?: Optional port number
   * - $: End of string
   */
  let protocol = /^(localhost(:\d+)?|dev\.[^.]+\.?[^.]+\.com(:\d+)?)$/.test(
    host,
  )
    ? "http:"
    : "https:";

  // Check forwarded headers for both request types
  if (req) {
    const forwardedHostHeader = getFirstXForwarded(req, "host");
    if (forwardedHostHeader) {
      host = forwardedHostHeader;
    }
    const forwardedProtoHeader = getFirstXForwarded(req, "proto");
    if (forwardedProtoHeader) {
      protocol = `${forwardedProtoHeader}:`;
    }
  }

  return {
    protocol,
    host,
    origin: protocol + "//" + host,
  };
}

/**
 * These are helpers being used in the constants files to determine defaults.
 * These helpers can't be in the `utils` folder because that might induce
 * recursive imports.
 */

// TODO this is a very rough check that misses many common cases. Strategy from SBN here:
// https://github.com/voxmedia/sbn/blob/master/lib/sbn_url_schema.rb#L53
export const isApexDomain = (domain: string) => {
  const periodRegex = /\./g;
  const periods = domain.match(periodRegex);

  return periods !== null && periods.length === 1;
};

/**
 * Extracts the community subdomain from a given host.
 *
 * NOTE: This function was used for PoC testing of Eater and has not been tested
 * extensively. This should be tested in various environments and against more cases
 * before use in production.
 */
export const getCommunitySubdomain = (host?: string | null) => {
  if (!host && typeof window !== "undefined") {
    // On client side, get the host from window
    host = window.location.host;
  }

  if (!host) {
    return null;
  }

  const parts = host.split(".");

  /**
   * For local dev environments, the host looks like `dev.austin.eater.com`.
   * for communities and `dev.eater.com` for the main community. Return the
   * second (index 1) part which will either be the community subdomain or
   * the main community.
   */
  if (parts[0] === "dev") {
    return parts[1];
  }

  /**
   * This should cover all other cases of non-dev environments.
   *
   * austin.eater.com -> austin
   * www.eater.com -> www (defaults to main community)
   * austin.pre-production.eater.com -> austin
   * pre-production.eater.com -> pre-production (defaults to main community)
   * austin.rc-staging.eater.com -> austin
   * rc-staging.eater.com -> (defaults to main community)
   */
  return parts[0];
};

export const getSearchParamsForPath = (path: string): URLSearchParams => {
  // Create a temporary URL object with a dummy domain
  const url = new URL("https://example.com" + path);

  return new URLSearchParams(url.search);
};

export const isPreviewPath = (path: string | undefined): boolean =>
  path ? getSearchParamsForPath(path).get("preview") === "true" : false;

export const getInternalApiURL = (req: Request, communityDomain: string) => {
  const path = "/api";

  if (process.env.INTERNAL_SERVICE_ORIGIN) {
    return new URL(path, process.env.INTERNAL_SERVICE_ORIGIN);
  }

  return reconstructAbsoluteURL({
    req,
    path,
    communityDomain,
  });
};

// RegExp to find url patterns.
export const URL_HTTP_PATTERN =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g;

/**
 * Gets the current origin from either a NextRequest or IncomingMessage/NextApiRequest
 */
export function getCurrentOrigin(
  req: NextRequest | IncomingMessage | NextApiRequest,
): string {
  return absoluteURL(req).origin;
}
